import React from "react";
import {observer} from "mobx-react-lite";
import {FormControlLabel, Stack, Switch, Typography} from "@mui/material";
import {useStores} from "~/stores";
import type {AdvancedOpportunityData, AdvancedSupplierBreakdownData, CategoryFilter} from "~/services/ApiTypes";
import {MultiSelection} from "~/components/visualization/api-dropdown-filter/ApiDropdownTypes";
import {toTrimmedCategoryArray} from "~/services/classes/CategoryHelpers";
import {DownloadDataButton} from "~/components/download-data-button/DownloadDataButton";
import {downloadAsFile} from "~/components/download-data-button/DownloadAsFile";
import {analyticsExportEvent} from "~/services/analytics/AnalyticEvents";
import {ByParentSupplierSwitch} from "~/pages/spend-landscape/components/ByParentSupplierSwitch";
import {SpendLandscapeGrid} from "./SpendLandscapeGrid";
import type {ColumnContext} from "./spendLandscapeColumns";
import {businessUnitPivotColumns, opportunityColumns, supplierBreakdownColumns} from "./spendLandscapeColumns";
import {businessUnitPivotExcelColumns, supplierBreakdownExcelColumns} from "./excelColumns";

const plural = (n: number, one: string, many: string) => `${n.toLocaleString()} ${n === 1 ? one : many}`;

function samePath(selected: CategoryFilter | null, row: {active_l1?: string, active_l2?: string, active_l3?: string, active_l4?: string}) {
    if (!selected) return false;
    const a = toTrimmedCategoryArray(selected);
    const b = toTrimmedCategoryArray([row.active_l1, row.active_l2, row.active_l3, row.active_l4]);
    return a.length > 0 && a.length === b.length && a.every((c, i) => c === b[i]);
}

const SectionHeader: React.FC<{title: string, children?: React.ReactNode}> = ({title, children}) =>
    <Stack direction="row" alignItems="center" flexWrap="wrap" gap={2} className="spend-landscape-section-header">
        <Typography variant="h6" component="h2" fontWeight={600}>{title}</Typography>
        <span style={{flexGrow: 1}}/>
        {children}
    </Stack>;

const SwitchControl: React.FC<{label: string, checked: boolean, onChange: (checked: boolean) => void}> = ({label, checked, onChange}) =>
    <FormControlLabel
        className="spend-landscape-switch"
        control={<Switch size="small" checked={checked} onChange={(_e, c) => onChange(c)}/>}
        label={<Typography variant="body2">{label}</Typography>}/>;

/**
 * The tables of the Spend Landscape page: category opportunities, suppliers per category and (when configured)
 * spend per business unit. Clicking a row filters the page on it.
 */
export const SpendLandscapeTables: React.FC<{
    /** Shown between the opportunities and the supplier table */
    afterOpportunities?: React.ReactNode
}> = observer(({afterOpportunities}) => {
    const {p, bagStore, synergyStore, authStore, materializedApi} = useStores();
    const manager = bagStore.advanced;
    const databagId = bagStore.bagId;
    const parentLabel = p.p.parentSupplierAltName ?? 'Parent supplier';
    const hasParentSuppliers = Boolean(bagStore.bag?.has_supplier_normalization_results);
    const isSynergy = Boolean(synergyStore.isSynergyDatabag);
    const enableCommonCategorySwitch = isSynergy && !p.p.disableCommonCategoryAdvDashboard;
    const enableCommonSupplierSwitch = hasParentSuppliers && isSynergy && !p.p.disableCommonSuppliersAdvDashboard;

    const ctx: ColumnContext = {
        showL4: Boolean(p.p.showL4InOpportunityDashboard),
        hideParents: Boolean(p.p.hideParentSupplierOpportunityDashboard),
        byParent: manager.groupParentSuppliersFilter,
        parentLabel,
        showSupplierId: Boolean(p.p.showSupplierIdInAdvDashboard),
        showBusinessUnits: Boolean(p.p.synergyDashboardShowBusinessUnits),
        buLabel: p.buDataName,
        showCDescription: Boolean(p.p.synergyDashboardShowCDescriptionFilter),
        cDescriptionLabel: p.cDescriptionDataName,
        showSource: isSynergy && p.opportunitySplitAllowed,
        showDatasetName: Boolean(p.p.synergyDashboardShowDatabagName),
        isSynergy,
        getBagName: id => bagStore.getBagName(id),
    };
    const selectedPath = manager.singleSelectedCategory;
    const supplierSelection = ctx.byParent ? manager.filters.sp_ids : manager.filters.s_ids;
    const isSupplierRowSelected = (row: AdvancedSupplierBreakdownData) =>
        MultiSelection.isIncludingOne(supplierSelection)
        && String(supplierSelection?.selected[0].id) === String(ctx.byParent ? row.sp_id : row.s_id)
        && samePath(selectedPath, row);

    const supplierColumns = supplierBreakdownColumns(ctx);
    const pivotRows = (manager.advancedBusinessUnitPivotDataRM.result?.data.rows ?? []) as unknown as Record<string, unknown>[];
    const pivotColumns = businessUnitPivotColumns(ctx, manager.advancedBusinessUnitPivotColumns ?? [], pivotRows);
    const dataName = bagStore.bag?.name || `Dataset ${databagId}`;

    const downloadSuppliers = () => {
        analyticsExportEvent({export_type: 'adv_dash_supplies_excel', object_key: String(databagId), user: authStore.email});
        const filename = `${dataName}.xlsx`;
        return materializedApi.downloadInsightsExcel(databagId, filename, supplierBreakdownExcelColumns(supplierColumns, isSynergy),
            manager.filterQueryParams, '', window.location.href)
            .then(resp => downloadAsFile(resp, filename));
    };
    const downloadPivot = () => {
        analyticsExportEvent({export_type: 'adv_dash_bu_pivot_excel', object_key: String(databagId), user: authStore.email});
        const filename = `${dataName} ${p.buDataName}.xlsx`;
        return materializedApi.downloadAdvancedBusinessUnitPivot(databagId, filename, businessUnitPivotExcelColumns(pivotColumns, isSynergy),
            manager.filterQueryParams, '', window.location.href)
            .then(resp => downloadAsFile(resp, filename));
    };

    return <Stack gap={4} className="spend-landscape-tables">
        <section>
            <SectionHeader title="Category specific opportunities">
                {enableCommonCategorySwitch && <SwitchControl
                    label="Only common categories"
                    checked={manager.filters.only_common_categories ?? false}
                    onChange={c => manager.setOnlyCommonCategoriesFilter(c)}/>}
            </SectionHeader>
            <SpendLandscapeGrid<AdvancedOpportunityData & Record<string, unknown>>
                layoutKey="spend-landscape-opportunities"
                columns={opportunityColumns(ctx)}
                requestManager={manager.advancedOpportunityDataRM as never}
                page={manager.advancedOpportunityDataPage}
                onPageChange={manager.setAdvancedOpportunityDataPage}
                perPage={manager.advancedOpportunityDataPerPage}
                onPerPageChange={manager.setAdvancedOpportunityDataPerPage}
                orderingPrefix="opportunity_"
                countLabel={n => plural(n, 'category', 'categories')}
                onRowClick={row => manager.breakdownFilterChanged(row as never)}
                isRowSelected={row => samePath(selectedPath, row)}
            />
        </section>

        {afterOpportunities}

        <section>
            <SectionHeader title="Supplier and spend category overview">
                {enableCommonSupplierSwitch && <SwitchControl
                    label="Only common suppliers"
                    checked={manager.filters.only_common_suppliers ?? false}
                    onChange={c => manager.setOnlyCommonSuppliersFilter(c)}/>}
                {hasParentSuppliers && <ByParentSupplierSwitch label={`By ${parentLabel.toLowerCase()}`}/>}
                <DownloadDataButton
                    dataLoading={manager.advancedSupplierBreakdownDataRM.busy}
                    dataSize={manager.advancedSupplierBreakdownDataRM.result?.data.count ?? 0}
                    onClick={downloadSuppliers}/>
            </SectionHeader>
            <SpendLandscapeGrid<AdvancedSupplierBreakdownData & Record<string, unknown>>
                layoutKey="spend-landscape-suppliers"
                columns={supplierColumns}
                requestManager={manager.advancedSupplierBreakdownDataRM as never}
                page={manager.advancedSupplierBreakdownPage}
                onPageChange={manager.setAdvancedSupplierBreakdownPage}
                perPage={manager.advancedSupplierBreakdownPerPage}
                onPerPageChange={manager.setAdvancedSupplierBreakdownPerPage}
                orderingPrefix="sup_breakdown_"
                countLabel={n => ctx.byParent ? plural(n, parentLabel.toLowerCase(), `${parentLabel.toLowerCase()}s`) : plural(n, 'supplier', 'suppliers')}
                onRowClick={row => manager.breakdownFilterChanged(row)}
                isRowSelected={isSupplierRowSelected}
            />
        </section>

        {ctx.showBusinessUnits && <section>
            <SectionHeader title={`${p.buDataName} overview`}>
                {hasParentSuppliers && <ByParentSupplierSwitch label={`By ${parentLabel.toLowerCase()}`}/>}
                <DownloadDataButton
                    dataLoading={manager.advancedBusinessUnitPivotDataRM.busy}
                    dataSize={manager.advancedBusinessUnitPivotDataRM.result?.data.count ?? 0}
                    onClick={downloadPivot}/>
            </SectionHeader>
            <SpendLandscapeGrid<AdvancedSupplierBreakdownData & Record<string, unknown>>
                layoutKey="spend-landscape-business-units"
                columns={pivotColumns}
                requestManager={manager.advancedBusinessUnitPivotDataRM as never}
                page={manager.advancedBusinessUnitPivotDataPage}
                onPageChange={manager.setBusinessUnitPivotDataPage}
                perPage={manager.advancedBusinessUnitPivotDataPerPage}
                onPerPageChange={manager.setBusinessUnitPivotDataPerPage}
                orderingPrefix="bu_pivot_"
                countLabel={n => `${n.toLocaleString()} of ${(manager.kpi?.n_suppliers ?? 0).toLocaleString()} suppliers in scope`}
                onRowClick={row => manager.breakdownFilterChanged(row)}
                isRowSelected={isSupplierRowSelected}
            />
        </section>}
    </Stack>;
});
